import { useState, useRef, useEffect } from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import clsx from 'clsx'

interface Secenek {
  id: string
  label: string
  alt?: string
}

interface MultiSelectProps {
  secenekler: Secenek[]
  secili: string[]
  onChange: (yeniSecili: string[]) => void
  placeholder?: string
  bos?: string
  disabled?: boolean
}

export function MultiSelect({
  secenekler,
  secili,
  onChange,
  placeholder = 'Seç...',
  bos = 'Henüz öğe yok',
  disabled = false,
}: MultiSelectProps) {
  const [acik, setAcik] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setAcik(false)
      }
    }
    if (acik) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [acik])

  function toggle(id: string) {
    if (secili.includes(id)) {
      onChange(secili.filter(s => s !== id))
    } else {
      onChange([...secili, id])
    }
  }

  function kaldir(id: string, e: React.MouseEvent) {
    e.stopPropagation()
    onChange(secili.filter(s => s !== id))
  }

  const seciliSecenekler = secenekler.filter(s => secili.includes(s.id))

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setAcik(!acik)}
        disabled={disabled}
        className={clsx(
          'input-base text-left flex items-center justify-between gap-2 min-h-[48px]',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <div className="flex flex-wrap gap-1 flex-1 min-w-0">
          {seciliSecenekler.length === 0 ? (
            <span className="text-gray-400">{placeholder}</span>
          ) : (
            seciliSecenekler.map(s => (
              <span
                key={s.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-100 text-brand-700 text-xs rounded-full"
              >
                {s.label}
                <span
                  onClick={e => kaldir(s.id, e)}
                  className="hover:bg-brand-200 rounded-full p-0.5 cursor-pointer"
                >
                  <X className="w-3 h-3" />
                </span>
              </span>
            ))
          )}
        </div>
        <ChevronDown className={clsx('w-4 h-4 text-gray-400 transition-transform flex-shrink-0', acik && 'rotate-180')} />
      </button>

      {acik && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {secenekler.length === 0 ? (
            <div className="p-3 text-center text-sm text-gray-500">{bos}</div>
          ) : (
            secenekler.map(s => {
              const checked = secili.includes(s.id)
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggle(s.id)}
                  className={clsx(
                    'w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2',
                    checked && 'bg-brand-50'
                  )}
                >
                  <div
                    className={clsx(
                      'w-5 h-5 border-2 rounded flex items-center justify-center flex-shrink-0',
                      checked ? 'bg-brand-600 border-brand-600' : 'border-gray-300'
                    )}
                  >
                    {checked && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{s.label}</p>
                    {s.alt && <p className="text-xs text-gray-500 truncate">{s.alt}</p>}
                  </div>
                </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
